import { StarIcon } from '@heroicons/react/24/outline';
import HistoryIcon from '../../assets/icons/history.svg?react';
import Logo from '../../assets/logo-same-color.svg?react';
import LogoSimple from '../../assets/logo-simple.svg?react';
import classNames from 'classnames';
import { memo, MouseEventHandler, useCallback, useContext, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useLocalStorage } from 'usehooks-ts';
import useQueryParams from '../../hooks/useQueryParams';
import { OrganisationSelect } from '../../modules/organisation/OrganisationSelect';
import { AccountSelect } from '../../modules/user/AccountSelect';
import { NotificationsDropdownButton } from '../../modules/renderer/RendererHeader/NotificationsDropdownButton';
import { T } from '../../translation/src';
import { useDeviceSize } from '../../hooks/useDeviceSize';
import { HeroIcon } from '../../types';
import { UserContext } from '../../contexts/UserContext';
import { ADMIN_PAGES, canAccessAdminPage, OBJECT_LIBRARY_PAGE, TEMPLATE_LIBRARY_PAGE, PARTNER_PAGES, REFERRALS_PAGE } from '../../modules/organisation/adminPages';
import { isPartnerRole, ordersForCustomers } from '../../modules/organisation/referrals/partnerRoles';

const StackSvg: React.FC2 = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M6 6.878V6a2.25 2.25 0 012.25-2.25h7.5A2.25 2.25 0 0118 6v.878m-12 0c.235-.083.487-.128.75-.128h10.5c.263 0 .515.045.75.128m-12 0A2.25 2.25 0 004.5 9v.878m13.5-3A2.25 2.25 0 0119.5 9v.878m0 0a2.246 2.246 0 00-.75-.128H5.25c-.263 0-.515.045-.75.128m15 0A2.25 2.25 0 0121 12v6a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 18v-6c0-.98.626-1.813 1.5-2.122"
    />
  </svg>
);

interface NavigationItemProps {
  icon: React.FC2 | HeroIcon;
  active?: boolean;
  onClick?: MouseEventHandler<HTMLDivElement>;
  smallDevice?: boolean;
}
const NavigationItem_: React.FC2<NavigationItemProps> = ({ icon: Icon, children, active, smallDevice, ...props }) => {
  return (
    <div
      className={classNames(
        'w-full cursor-pointer h-14 items-center text-eerie-black flex px-4 font-semibold dark:text-white ',
        active ? 'bg-neon-green-300 text-ash-green-800 dark:bg-black dark:text-white' : 'hover:marker:font-bold hover:bg-neon-green-300 dark:hover:bg-black dark:border-gray-400',
      )}
      {...props}
    >
      <div className={'flex items-center'}>
        <Icon className={smallDevice ? 'w-5 ml-3' : 'w-4 mr-4'} />
        <div>{children}</div>
      </div>
    </div>
  );
};
const NavigationItem = memo(NavigationItem_);

interface AdminNavigationProps {
  organisationId: string;
  smallDevice: boolean;
}
/**
 * Organisation admin links, shown as a separate "Admin" / "Super admin" block below the project links.
 * Lives in the sidebar rather than the organisation dropdown so the current page stays visible while
 * switching organisations, and so it is obvious in what capacity the user is on this organisation.
 */
const AdminNavigation_: React.FC2<AdminNavigationProps> = ({ organisationId, smallDevice }) => {
  const currentUser = useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();

  const organisation = useMemo(() => currentUser.organisations.find((o) => o.id === organisationId), [currentUser.organisations, organisationId]);
  const myPermissions = useMemo(() => organisation?.myPermissions || [], [organisation]);
  // A partner's referral pages are a block of their own (see PartnerNavigation).
  const partner = isPartnerRole(organisation?.referral.role);
  const pages = useMemo(() => ADMIN_PAGES.filter((page) => canAccessAdminPage(page, myPermissions, currentUser.isSuperAdmin) && !(partner && page.path === REFERRALS_PAGE.path)), [currentUser.isSuperAdmin, myPermissions, partner]);
  // Admin in their own right on this organisation, i.e. through membership rather than super-admin rights.
  const isOrganisationAdmin = useMemo(() => ADMIN_PAGES.some((page) => canAccessAdminPage(page, myPermissions, false)), [myPermissions]);

  if (!organisationId || pages.length === 0) return null;

  return (
    <>
      {/* Room on both sides of the line, as the menu's other dividers have: not right under the last project item. */}
      <div className={classNames('border-t border-gray-400', smallDevice ? 'mt-2 h-2' : 'mt-8 h-8')} />
      {!smallDevice && (
        <div className="px-4 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-neon-green-300">
          {currentUser.isSuperAdmin && (
            <span className="text-error-red-500">
              <T _str="super admin" swc />
            </span>
          )}
          {currentUser.isSuperAdmin && isOrganisationAdmin && <span> · </span>}
          {isOrganisationAdmin && <T _str="admin" swc />}
        </div>
      )}
      {pages.map((page) => (
        <NavigationItem
          key={page.path}
          icon={page.icon}
          active={location.pathname.startsWith(`/organisations/${organisationId}/${page.path}`)}
          onClick={() => navigate(`/organisations/${organisationId}/${page.path}`)}
          smallDevice={smallDevice}
        >
          {!smallDevice && <T _str={page.label} swc />}
        </NavigationItem>
      ))}
    </>
  );
};
const AdminNavigation = memo(AdminNavigation_);

/**
 * A partner's program (lead generator, reseller, full-service partner) as its own block at the top of the sidebar,
 * each page its own item. A customer finds its referrals among the admin pages instead.
 */
const PartnerNavigation_: React.FC2<AdminNavigationProps> = ({ organisationId, smallDevice }) => {
  const currentUser = useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();

  const organisation = useMemo(() => currentUser.organisations.find((o) => o.id === organisationId), [currentUser.organisations, organisationId]);
  const role = organisation?.referral.role;
  const canAccess = canAccessAdminPage(REFERRALS_PAGE, organisation?.myPermissions || [], currentUser.isSuperAdmin);
  const pages = useMemo(() => PARTNER_PAGES.filter((page) => page.forRole(role)), [role]);

  if (!organisationId || !isPartnerRole(role) || !canAccess) return null;

  return (
    <>
      {!smallDevice && (
        <div className="px-4 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-neon-green-300">
          <T _str="partner program" swc />
        </div>
      )}
      {pages.map((page) => (
        <NavigationItem key={page.path} icon={page.icon} active={location.pathname === `/organisations/${organisationId}/${page.path}`} onClick={() => navigate(`/organisations/${organisationId}/${page.path}`)} smallDevice={smallDevice}>
          {!smallDevice && <T _str={(ordersForCustomers(role) && page.ordersLabel) || page.label} swc />}
        </NavigationItem>
      ))}
      <div className={classNames('border-b border-gray-400', smallDevice ? 'mb-2 h-2' : 'mb-8 h-8')} />
    </>
  );
};
const PartnerNavigation = memo(PartnerNavigation_);

const Navigation_: React.FC2 = () => {
  const { projectsSelect } = useQueryParams(['projectsSelect']);
  const [lastSelectedOrganisation] = useLocalStorage('lastSelectedOrganisation', '');
  const navigate = useNavigate();
  const location = useLocation();
  const { organisationId = '' } = useParams();
  const { mdDevice } = useDeviceSize();

  const isOnProjectsPage = location.pathname.includes('/projects');

  // The object library sits with the projects, for those allowed to use it (see OBJECT_LIBRARY_PAGE).
  const currentUser = useContext(UserContext);
  const libraryOrganisationId = organisationId || lastSelectedOrganisation;
  const libraryPermissions = useMemo(() => currentUser.organisations.find((o) => o.id === libraryOrganisationId)?.myPermissions ?? [], [currentUser.organisations, libraryOrganisationId]);
  const showObjectLibrary = !!libraryOrganisationId && canAccessAdminPage(OBJECT_LIBRARY_PAGE, libraryPermissions, currentUser.isSuperAdmin);
  const showTemplateLibrary = !!libraryOrganisationId && canAccessAdminPage(TEMPLATE_LIBRARY_PAGE, libraryPermissions, currentUser.isSuperAdmin);

  const onClickNavigationItem = useCallback(
    ({ type }: { type: 'all' | 'recent' | 'favorites' }) => {
      if (organisationId === '' && !lastSelectedOrganisation) {
        navigate(`/no-organisation`);
      } else {
        navigate({
          ...location,
          pathname: `/organisations/${organisationId || lastSelectedOrganisation}/projects`,
          search: '?projectsSelect=' + type,
        });
      }
    },
    [location, navigate, organisationId, lastSelectedOrganisation],
  );

  return (
    <div className={classNames('flex flex-col flex-shrink-0 h-full py-2 bg-gray-50 border-r border-gray-400 dark:bg-[#242424]', mdDevice ? 'w-20' : 'w-72')}>
      {mdDevice ? <LogoSimple className="my-4 mr-4 h-7 text-eerie-black dark:text-white" width="100%" /> : <Logo className="px-4 my-8 text-eerie-black dark:text-white" width="100%" />}
      <div className={classNames('border-b border-gray-400', mdDevice ? 'h-2' : 'h-8')} />
      <OrganisationSelect size={mdDevice ? 'small' : 'large'} />
      <div className={classNames('border-t border-gray-400', mdDevice ? 'h-2' : 'h-8')} />
      <PartnerNavigation organisationId={organisationId || lastSelectedOrganisation} smallDevice={mdDevice} />

      <NavigationItem icon={StackSvg} active={isOnProjectsPage && (!projectsSelect || projectsSelect === 'all')} onClick={() => onClickNavigationItem({ type: 'all' })} smallDevice={mdDevice}>
        {!mdDevice && <T _str="all projects" swc />}
      </NavigationItem>
      <NavigationItem icon={HistoryIcon} active={isOnProjectsPage && projectsSelect === 'recent'} onClick={() => onClickNavigationItem({ type: 'recent' })} smallDevice={mdDevice}>
        {!mdDevice && <T _str="recent projects" swc />}
      </NavigationItem>
      <NavigationItem icon={StarIcon} active={isOnProjectsPage && projectsSelect === 'favorites'} onClick={() => onClickNavigationItem({ type: 'favorites' })} smallDevice={mdDevice}>
        {!mdDevice && <T _str="favorite projects" swc />}
      </NavigationItem>
      {showObjectLibrary && (
        <NavigationItem
          icon={OBJECT_LIBRARY_PAGE.icon}
          active={location.pathname.startsWith(`/organisations/${libraryOrganisationId}/${OBJECT_LIBRARY_PAGE.path}`)}
          onClick={() => navigate(`/organisations/${libraryOrganisationId}/${OBJECT_LIBRARY_PAGE.path}`)}
          smallDevice={mdDevice}
        >
          {!mdDevice && <T _str={OBJECT_LIBRARY_PAGE.label} swc />}
        </NavigationItem>
      )}
      {showTemplateLibrary && (
        <NavigationItem
          icon={TEMPLATE_LIBRARY_PAGE.icon}
          active={location.pathname.startsWith(`/organisations/${libraryOrganisationId}/${TEMPLATE_LIBRARY_PAGE.path}`)}
          onClick={() => navigate(`/organisations/${libraryOrganisationId}/${TEMPLATE_LIBRARY_PAGE.path}`)}
          smallDevice={mdDevice}
        >
          {!mdDevice && <T _str={TEMPLATE_LIBRARY_PAGE.label} swc />}
        </NavigationItem>
      )}
      <AdminNavigation organisationId={organisationId || lastSelectedOrganisation} smallDevice={mdDevice} />
      <div className={classNames('flex justify-end grow', mdDevice ? 'flex-col-reverse justify-start items-center gap-2' : 'flex-row-reverse items-end gap-2 pl-2')}>
        <div className={classNames(mdDevice ? 'w-full' : 'flex-1 min-w-0')}>
          <AccountSelect variant="navigation" forceReload={false} size={mdDevice ? 'small' : 'large'} />
        </div>
        <div className={classNames(!mdDevice && 'pb-1.5')}>
          <NotificationsDropdownButton anchor="right end" />
        </div>
      </div>
    </div>
  );
};

export const Navigation = memo(Navigation_);
